import { Injectable } from '@angular/core';
import { Router, NavigationStart, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { BsModalService, BsModalRef, ModalOptions } from 'ngx-bootstrap/modal';
import { AudioSyncService } from './audio-sync.service';

@Injectable({
  providedIn: 'root'
})
export class RecordingNavigationGuardService {
  private modalRef: BsModalRef | null = null;
  private pendingNavigation: any = null;
  private destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private modalService: BsModalService,
    private audioSyncService: AudioSyncService
  ) {
    this.setupNavigationInterception();
  }

  private setupNavigationInterception(): void {
    this.router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        if (event instanceof NavigationStart) {
          this.handleNavigationStart(event);
        } else if (event instanceof NavigationEnd || 
                   event instanceof NavigationCancel || 
                   event instanceof NavigationError) {
          this.pendingNavigation = null;
        }
      });
  }

  private handleNavigationStart(event: NavigationStart): void {
    // Check if recording is in progress
    if (this.audioSyncService.currentRecordingState) {
      // Store the pending navigation
      this.pendingNavigation = event;
      
      // Prevent the navigation
      this.router.navigateByUrl(this.router.url, { skipLocationChange: true });
      
      // Show the recording dialog
      this.showRecordingDialog();
    }
  }

  private showRecordingDialog(): void {
    if (this.modalRef) {
      return; // Dialog already open
    }

    const modalOptions: ModalOptions = {
      class: 'modal-dialog-centered',
      backdrop: 'static',
      keyboard: false,
      ignoreBackdropClick: true
    };

    // Create modal content
    const modalContent = `
      <div class="modal-header">
        <h4 class="modal-title">
          <i class="fa fa-microphone text-danger me-2"></i>
          Recording in Progress
        </h4>
      </div>
      <div class="modal-body">
        <p class="mb-3">
          Are you sure you want to stop the recording?
        </p>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-primary" id="continue-recording">
          <i class="fa fa-play me-2"></i>
          Continue Recording
        </button>
        <button type="button" class="btn btn-danger" id="stop-recording">
          <i class="fa fa-stop me-2"></i>
          Stop Recording
        </button>
      </div>
    `;

    // Create modal element
    const modalElement = document.createElement('div');
    modalElement.className = 'modal fade show';
    modalElement.style.display = 'block';
    modalElement.innerHTML = `
      <div class="modal-dialog">
        <div class="modal-content">
          ${modalContent}
        </div>
      </div>
    `;

    // Add backdrop
    const backdrop = document.createElement('div');
    backdrop.className = 'modal-backdrop fade show';
    document.body.appendChild(backdrop);
    document.body.appendChild(modalElement);

    // Handle button clicks
    const continueBtn = modalElement.querySelector('#continue-recording');
    const stopBtn = modalElement.querySelector('#stop-recording');

    continueBtn?.addEventListener('click', () => {
      this.closeDialog(modalElement, backdrop);
    });

    stopBtn?.addEventListener('click', () => {
      this.stopRecordingAndNavigate(modalElement, backdrop);
    });

    // Store references for cleanup
    this.modalRef = {
      hide: () => this.closeDialog(modalElement, backdrop),
      content: {
        onConfirm: new Subject<any>()
      }
    } as any;
  }

  private closeDialog(modalElement: HTMLElement, backdrop: HTMLElement): void {
    modalElement.remove();
    backdrop.remove();
    this.modalRef = null;
    this.pendingNavigation = null;
  }

  private stopRecordingAndNavigate(modalElement: HTMLElement, backdrop: HTMLElement): void {
    // Stop the recording
    this.audioSyncService.stopMediaRecorder();
    
    // Close the dialog
    this.closeDialog(modalElement, backdrop);
    
    // Navigate to the pending route
    if (this.pendingNavigation) {
      this.router.navigateByUrl(this.pendingNavigation.url);
    }
  }

  // Method to manually check and block navigation (for button clicks)
  public checkAndBlockNavigation(callback: () => void): void {
    if (this.audioSyncService.currentRecordingState) {
      this.showRecordingDialog();
    } else {
      callback();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
